// src/app/features/conversations/FloatingChat/useDraggable.ts
import { useState, useRef } from 'react';

/**
 * A custom hook to handle "pointer" dragging.
 * - Returns (pos, onPointerDown, onPointerMove, onPointerUp) so you can attach them to a component.
 * - Also includes a minimal "click threshold" so small movements are treated like a click.
 */
export interface UseDraggableOptions {
  buttonSize?: number; // e.g. 30
  clickThreshold?: number; // e.g. 5 px
}

interface IPoint {
  x: number;
  y: number;
}

export function useDraggable({
  buttonSize = 30,
  clickThreshold = 5,
}: UseDraggableOptions = {}) {
  // Position in px from top-left
  const [pos, setPos] = useState<IPoint>({ x: 0, y: 0 });
  // Track whether we are actively dragging
  const [isDragging, setIsDragging] = useState(false);
  // Where did the user initially press down?
  const pointerDownRef = useRef<IPoint | null>(null);

  // We also track whether the "click threshold" was exceeded
  const [isClick, setIsClick] = useState(false);

  // On pointer down => begin capturing
  const onPointerDown = (e: React.PointerEvent<HTMLElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    setIsDragging(true);
    // Remember pointer-down coordinates
    pointerDownRef.current = { x: e.clientX, y: e.clientY };
    // Initially assume it's a click; if they move far, we set false
    setIsClick(true);
  };

  // On pointer move => if dragging, update position
  const onPointerMove = (e: React.PointerEvent<HTMLElement>) => {
    if (!isDragging) return;

    // Check how far user has moved from pointerDown
    if (pointerDownRef.current) {
      const dx = e.clientX - pointerDownRef.current.x;
      const dy = e.clientY - pointerDownRef.current.y;
      const distance = Math.sqrt(dx * dx + dy * dy);

      // If distance is bigger than threshold, it's a drag, not a click
      if (distance > clickThreshold) {
        setIsClick(false);
      }
    }

    // Center the element under the cursor
    const halfBtn = buttonSize / 2;
    const newX = e.clientX - halfBtn;
    const newY = e.clientY - halfBtn;

    // Optionally clamp so it doesn't go fully offscreen
    const maxX = window.innerWidth - buttonSize;
    const maxY = window.innerHeight - buttonSize;
    const clampedX = Math.max(0, Math.min(newX, maxX));
    const clampedY = Math.max(0, Math.min(newY, maxY));

    setPos({ x: clampedX, y: clampedY });
  };

  // On pointer up => stop dragging; if movement was small => "click"
  const onPointerUp = (e: React.PointerEvent<HTMLElement>) => {
    e.currentTarget.releasePointerCapture(e.pointerId);
    setIsDragging(false);
  };

  return {
    pos,
    isClick,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    setPos, // might be useful for initial layout
  };
}
